import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Input } from "@repo/ui/Inputs/input";
import { type FC, useState } from "react";
import { useUpdateMyProfileMutation } from "../../api/entities/teammate.js";
import { useAppDispatch, useAppSelector } from "../../hooks/hook.js";
import { selectTeammateRole } from "../miscellaneous/authorizationSlice.js";
import {
  NotificationType,
  showNotification,
} from "../notificationsOverlay/notificationsSlice.js";
import { selectTeammate } from "../teammate/teammateSlice.js";
import { settingsSections } from "./settingsSections.js";
import { SettingsSectionPage } from "./shared/settingsSection.js";

const ProfileSettingsSection: FC = () => {
  const dispatch = useAppDispatch();
  const teammate = useAppSelector(selectTeammate);
  const [updateMyProfile] = useUpdateMyProfileMutation();
  if (!teammate) return null;

  const teammateRole = useAppSelector(selectTeammateRole);

  const [firstName, setFirstName] = useState(teammate.first_name || "");
  const [lastName, setLastName] = useState(teammate.last_name || "");
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");

  const onClickSave = async () => {
    // We need a first name and last name
    if (!firstName) setFirstNameError("First name is required");
    if (!lastName) setLastNameError("Last name is required");

    // If any of the fields are missing, let user fill in
    if (!firstName || !lastName) return;

    // Update the teammate first name and last name if necessary
    if (teammate.first_name !== firstName || teammate.last_name !== lastName) {
      try {
        await updateMyProfile({
          first_name: firstName,
          last_name: lastName,
        }).unwrap();

        showNotification(dispatch, {
          id: `profile-updated-${new Date().getTime()}`,
          primaryMessage: `Profile successfully updated`,
          type: NotificationType.SUCCESS,
        });
      } catch (error) {
        console.error("Failed to update profile", error);
        showNotification(dispatch, {
          id: `profile-updated-${new Date().getTime()}`,
          primaryMessage: `Failed to update profile`,
          type: NotificationType.FAILURE,
        });
      }
    }
  };

  return (
    <SettingsSectionPage title={settingsSections.profile.label}>
      <div className="border-b border-gray-900/10 pb-8">
        <div className="mt-5 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
          <div className="sm:col-span-4">
            <Input
              label="Email address"
              onChange={() => {
                /* noop for disabled input*/
              }}
              value={teammate.email}
              readOnly
              isDisabled
              maxLength={100}
            />
          </div>
        </div>
        <div className="mt-5 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
          <div className="sm:col-span-3">
            <Input
              label="First name"
              value={firstName}
              onChange={(evt) => {
                setFirstNameError("");
                setFirstName(evt.target.value);
              }}
              autoComplete="given-name"
              errorDescription={firstNameError}
              maxLength={100}
            />
          </div>
          <div className="sm:col-span-3">
            <Input
              label="Last name"
              value={lastName}
              onChange={(evt) => {
                setLastNameError("");
                setLastName(evt.target.value);
              }}
              autoComplete="family-name"
              errorDescription={lastNameError}
              maxLength={100}
            />
          </div>
        </div>
        {teammateRole && (
          <div className="mt-5 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
            <div className="sm:col-span-4">
              <Input
                label="Role"
                onChange={() => {
                  /* noop for disabled input*/
                }}
                value={teammateRole.role_name}
                readOnly
                isDisabled
                maxLength={100}
              />
            </div>
          </div>
        )}
      </div>
      <div className="mt-6 flex items-center justify-end gap-x-6">
        <Button
          type="button"
          onClick={onClickSave}
          variant={ButtonVariant.Primary}
        >
          Save
        </Button>
      </div>
    </SettingsSectionPage>
  );
};

export default ProfileSettingsSection;
